import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { FORM_PHP_BASE_URL } from "../../config/config";
import { questionnaireApi } from "../../data/api/QuestionnaireApi";
import {
  QuestionnaireEmbedMode,
  QuestionnaireEmbedTokenResponse,
} from "../../data/models/Questionnaire/QuestionnaireModel";

type EmbedEventPayload = {
  source?: string;
  type?: string;
  payload?: any;
};

const EMBED_SOURCE = "convisas-questionnaire-embed";

const toNumberOrNull = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") return null;
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
};

type QuestionnaireEmbedChrome = "full" | "compact";

const buildUrl = (
  baseUrl: string,
  params: Record<string, string | number | null | undefined>,
) => {
  const target = new URL(baseUrl, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    target.searchParams.set(key, String(value));
  });
  return target.toString();
};

interface QuestionnaireEmbedFrameProps {
  path: string;
  mode: QuestionnaireEmbedMode;
  questionnaireCode: string;
  versionId?: string | number | null;
  chrome?: QuestionnaireEmbedChrome;
  height?: number | string;
  readOnly?: boolean;
  closable?: boolean;
  onReady?: (payload: any) => void;
  onSaved?: (payload: any) => void;
  onSubmitted?: (payload: any) => void;
  onProgress?: (payload: any) => void;
  onClose?: (payload: any) => void;
  onError?: (payload: any) => void;
}

const QuestionnaireEmbedFrame: React.FC<QuestionnaireEmbedFrameProps> = ({
  path,
  mode,
  questionnaireCode,
  versionId,
  chrome = "full",
  height = 820,
  readOnly = false,
  closable = true,
  onReady,
  onSaved,
  onSubmitted,
  onProgress,
  onClose,
  onError,
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [embedData, setEmbedData] = useState<QuestionnaireEmbedTokenResponse | null>(null);

  const numericVersionId = useMemo(
    () => toNumberOrNull(versionId),
    [versionId],
  );

  useEffect(() => {
    let cancelled = false;

    const loadBridgeToken = async () => {
      if (!questionnaireCode || !path) {
        setError("Falta el cuestionario o la ruta de embed");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setEmbedData(null);

      const result = await questionnaireApi.embedToken.request({
        mode,
        questionnaireCode,
        versionId: numericVersionId,
      });

      if (cancelled) {
        return;
      }

      if (!result.isSuccess || !result.data?.token) {
        setError(result.messageError ?? "No se pudo generar el acceso al embed");
        onError?.(result.messageError ?? "No se pudo generar el acceso al embed");
        setLoading(false);
        return;
      }

      setEmbedData(result.data);
      setLoading(false);
    };

    void loadBridgeToken();

    return () => {
      cancelled = true;
    };
  }, [mode, numericVersionId, onError, path, questionnaireCode]);

  const iframeSrc = useMemo(() => {
    if (!embedData?.token) return null;
    return buildUrl(path, {
      embed: 1,
      mode,
      questionnaireCode,
      versionId: numericVersionId,
      bridgeToken: embedData.token,
      chrome: chrome === "compact" ? "compact" : null,
      readOnly: readOnly ? 1 : 0,
      closable: closable ? 1 : 0,
    });
  }, [chrome, embedData?.token, closable, mode, numericVersionId, path, questionnaireCode, readOnly]);

  const expectedOrigin = useMemo(() => {
    try {
      return new URL(FORM_PHP_BASE_URL, window.location.origin).origin;
    } catch (_error) {
      return window.location.origin;
    }
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) return;
      const data = event.data as EmbedEventPayload | undefined;
      if (!data || data.source !== EMBED_SOURCE) return;

      switch (data.type) {
        case "ready":
          onReady?.(data.payload);
          break;
        case "saved":
          onSaved?.(data.payload);
          break;
        case "submitted":
          onSubmitted?.(data.payload);
          break;
        case "progress":
          onProgress?.(data.payload);
          break;
        case "close":
          onClose?.(data.payload);
          break;
        case "error":
          onError?.(data.payload);
          if (typeof data.payload === "string") {
            setError(data.payload);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [expectedOrigin, onClose, onError, onProgress, onReady, onSaved, onSubmitted]);

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height,
        overflow: "hidden",
        borderRadius: chrome === "compact" ? 0 : 3,
        border: chrome === "compact" ? 0 : "1px solid",
        borderColor: chrome === "compact" ? "transparent" : "divider",
        bgcolor: chrome === "compact" ? "transparent" : "background.paper",
      }}
    >
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            bgcolor: "background.paper",
            zIndex: 2,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Cargando formulario embebido...
          </Typography>
        </Box>
      )}

      {iframeSrc && (
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          title={`questionnaire-${mode}-${questionnaireCode}`}
          style={{
            width: "100%",
            height: "100%",
            border: 0,
            display: "block",
            background: "#fff",
          }}
          allow="clipboard-read; clipboard-write"
        />
      )}
    </Box>
  );
};

export default QuestionnaireEmbedFrame;
